import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { CTU_SPINOFF_COLOR, CTU_STARTUP_COLOR } from '../../lib/colors';

interface CTUCompaniesHeroProps {
  spinoffsCount: number;
  startupsCount: number;
  foundersCount: number;
}

const TONE_COLOR = {
  spinoff: CTU_SPINOFF_COLOR,
  startup: CTU_STARTUP_COLOR,
} as const;

type Tone = keyof typeof TONE_COLOR | 'default';

export default function CTUCompaniesHero({
  spinoffsCount,
  startupsCount,
  foundersCount,
}: CTUCompaniesHeroProps) {
  const { t } = useTranslation('ctuCompanies');

  const stats: { label: string; value: number; tone: Tone }[] = [
    { label: t('stats.spinoffs'), value: spinoffsCount, tone: 'spinoff' },
    { label: t('stats.startups'), value: startupsCount, tone: 'startup' },
    { label: t('stats.founders'), value: foundersCount, tone: 'default' },
  ];

  return (
    <Card className="mb-6 border-ctu-border shadow-none">
      <CardContent className="p-8">
        <h1 className="text-3xl font-semibold tracking-tight text-ctu-text mb-2">
          {t('hero.title')}
        </h1>
        <p className="text-base text-ctu-text-muted m-0">{t('hero.subtitle')}</p>

        <Separator className="my-6" />
        <dl className="grid grid-cols-3 gap-x-8 gap-y-4">
          {stats.map(({ label, value, tone }) => {
            const valueStyle =
              tone === 'default' ? undefined : { color: TONE_COLOR[tone] };
            return (
              <div key={label} className="flex flex-col gap-1">
                <dt className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted">
                  {label}
                </dt>
                <dd
                  className="text-2xl font-semibold tabular-nums text-ctu-text"
                  style={valueStyle}
                >
                  {value}
                </dd>
              </div>
            );
          })}
        </dl>
      </CardContent>
    </Card>
  );
}
